import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { Copy, Download, Eye, FileText, Languages, PanelLeft, Pencil, Plus, RotateCcw, Search, Trash2, Upload } from "lucide-react";
import { apiFetch, useApp } from "../../context/AppContext";
import { confirmAction } from "../../utils/sweetAlert";
import { Badge, Button, Input, Select } from "./UI";
import CollapsibleSettingsCard from "./CollapsibleSettingsCard";

const CATEGORIES = [
  { value: "regulatory", label: "Regulator notice" },
  { value: "principal", label: "Data principal notice" },
  { value: "processor", label: "Processor notice" },
  { value: "internal", label: "Internal brief" },
  { value: "other", label: "Other" },
];

const TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ align: [] }],
  [{ direction: "rtl" }],
  ["link", "clean"],
];

const EMPTY_HTML = "<p><br></p>";
const PLACEHOLDERS = [
  { token: "{{organisation_name}}", label: "Organisation" },
  { token: "{{incident_reference}}", label: "Reference" },
  { token: "{{discovered_at}}", label: "Discovered" },
  { token: "{{incident_date}}", label: "Incident date" },
  { token: "{{description}}", label: "What happened" },
  { token: "{{data_categories}}", label: "Data categories" },
  { token: "{{individuals_affected}}", label: "People affected" },
  { token: "{{consequences}}", label: "Consequences" },
  { token: "{{measures_taken}}", label: "Measures taken" },
  { token: "{{contact_name}}", label: "Contact name" },
  { token: "{{contact_email}}", label: "Contact email" },
  { token: "{{contact_phone}}", label: "Contact phone" },
];
const SAMPLE_VALUES = {
  organisation_name: "Acme Private Limited",
  incident_reference: "BR-2026-0042",
  discovered_at: "12 October 2026",
  incident_date: "10 October 2026",
  description: "Unauthorised access to a customer database",
  data_categories: "Name, email address, phone number",
  individuals_affected: "1,250",
  consequences: "Possible misuse of contact details",
  measures_taken: "Access revoked, passwords reset, monitoring increased",
  contact_name: "Priya Sharma",
  contact_email: "privacy@acme.example",
  contact_phone: "+91 98765 43210",
};
const DRAFT_STORAGE_KEY = "onedpdp.breachTemplateDrafts";

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fillSamplePlaceholders(text) {
  return String(text || "").replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_, key) => {
    const sample = SAMPLE_VALUES[String(key).toLowerCase()];
    return sample != null
      ? `<span class="sample-filled">${escapeHtml(sample)}</span>`
      : `{{${key}}}`;
  });
}

function readStoredDrafts() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(DRAFT_STORAGE_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function categoryLabel(value) {
  return CATEGORIES.find((item) => item.value === value)?.label || value;
}

function formatWhen(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

async function readError(res, fallback) {
  const data = await res.json().catch(() => ({}));
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) {
    return data.detail.map((item) => item.msg || item).join(", ");
  }
  return fallback;
}

function filenameFromResponse(res, fallback) {
  const header = res.headers.get("content-disposition") || "";
  const match = header.match(/filename="?([^";]+)"?/i);
  return match ? match[1] : fallback;
}

function removeQuillToolbars(root) {
  if (!root) return;
  root.querySelectorAll(":scope > .ql-toolbar").forEach((node) => node.remove());
}

function normalizeHref(value) {
  const trimmed = (value || "").trim();
  if (!trimmed || trimmed === "https://") return "";
  return /^(https?:|mailto:)/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

const RichTemplateEditor = forwardRef(function RichTemplateEditor({ initialHtml, dir, onChange, onReady }, ref) {
  const wrapRef = useRef(null);
  const hostRef = useRef(null);
  const quillRef = useRef(null);
  const linkRangeRef = useRef(null);
  const linkBoxRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onReadyRef = useRef(onReady);
  const initialRef = useRef(initialHtml);
  const dirRef = useRef(dir);
  const [linkBox, setLinkBox] = useState(null);
  onChangeRef.current = onChange;
  onReadyRef.current = onReady;
  dirRef.current = dir;
  linkBoxRef.current = linkBox;

  useImperativeHandle(ref, () => ({
    insertToken(token) {
      const quill = quillRef.current;
      if (!quill || !token) return false;
      const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1), length: 0 };
      if (range.length > 0) quill.deleteText(range.index, range.length, "user");
      quill.insertText(range.index, token, "user");
      quill.setSelection(range.index + token.length, 0, "user");
      quill.focus();
      return true;
    },
  }), []);

  useEffect(() => {
    const wrap = wrapRef.current;
    const host = hostRef.current;
    if (!wrap || !host) return undefined;

    // Toolbars left behind by an older mount sit on the parent, outside this wrapper.
    removeQuillToolbars(wrap.parentElement);
    removeQuillToolbars(wrap);

    const quill = new Quill(host, {
      theme: "snow",
      modules: { toolbar: TOOLBAR },
      placeholder: "Write the notice…",
    });
    quillRef.current = quill;
    const toolbar = quill.getModule("toolbar");
    toolbar.addHandler("link", () => {
      const range = quill.getSelection(true);
      if (!range) return;
      linkRangeRef.current = { index: range.index, length: range.length };
      const current = quill.getFormat(range).link || "";
      const button = wrap.querySelector("button.ql-link");
      const rect = button?.getBoundingClientRect();
      const width = 288;
      const left = Math.min(
        Math.max(8, rect ? rect.left : 16),
        window.innerWidth - width - 8
      );
      const top = Math.min((rect?.bottom ?? 80) + 6, window.innerHeight - 180);
      setLinkBox({ value: current || "https://", top, left, hadLink: Boolean(current) });
    });
    toolbar.addHandler("clean", () => {
      const range = quill.getSelection(true);
      if (!range) return;
      if (range.length === 0) {
        quill.removeFormat(0, quill.getLength());
      } else {
        quill.removeFormat(range.index, range.length);
      }
    });
    quill.clipboard.dangerouslyPasteHTML(initialRef.current || EMPTY_HTML);
    quill.root.setAttribute("dir", dirRef.current === "rtl" ? "rtl" : "ltr");
    onReadyRef.current?.(quill.root.innerHTML);
    const onText = () => onChangeRef.current?.(quill.root.innerHTML);
    quill.on("text-change", onText);
    return () => {
      quill.off("text-change", onText);
      quillRef.current = null;
      setLinkBox(null);
      removeQuillToolbars(wrap);
      host.innerHTML = "";
      host.className = "";
    };
  }, []);

  const closeLinkBox = () => setLinkBox(null);

  const applyLink = (remove) => {
    const quill = quillRef.current;
    const range = linkRangeRef.current;
    const box = linkBoxRef.current;
    if (!quill || !range || !box) {
      setLinkBox(null);
      return;
    }
    const href = remove ? "" : normalizeHref(box.value);
    quill.focus();
    if (!href) {
      if (range.length > 0) quill.formatText(range.index, range.length, "link", false);
    } else if (range.length === 0) {
      quill.insertText(range.index, href, "link", href);
    } else {
      quill.formatText(range.index, range.length, "link", href);
    }
    setLinkBox(null);
  };

  return (
    <div ref={wrapRef} className="breach-template-quill-host h-full min-h-0 flex flex-col">
      <div ref={hostRef} className="min-h-0 flex-1" />
      {linkBox && (
        <div
          className="fixed z-[80] w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-xl"
          style={{ top: linkBox.top, left: linkBox.left }}
        >
          <label className="mb-1.5 block text-xs font-medium text-slate-600" htmlFor="breach-template-link">
            Link address
          </label>
          <input
            id="breach-template-link"
            autoFocus
            value={linkBox.value}
            onChange={(event) => setLinkBox({ ...linkBox, value: event.target.value })}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                applyLink(false);
              }
              if (event.key === "Escape") closeLinkBox();
            }}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20"
            placeholder="https://"
          />
          <div className="mt-2.5 flex items-center justify-between gap-2">
            {linkBox.hadLink ? (
              <button type="button" className="text-xs font-medium text-red-600 hover:text-red-700" onClick={() => applyLink(true)}>
                Remove
              </button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={closeLinkBox}>Cancel</Button>
              <Button type="button" size="sm" onClick={() => applyLink(false)}>Add link</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default function BreachTemplatesPanel({
  apiBase = "/tenant/settings/breach-templates",
}) {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [language, setLanguage] = useState("en");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("regulatory");
  const [title, setTitle] = useState("");
  const [html, setHtml] = useState(EMPTY_HTML);
  const [saved, setSaved] = useState({
    name: "",
    description: "",
    category: "regulatory",
    title: "",
    html: EMPTY_HTML,
  });
  const [editorKey, setEditorKey] = useState(0);
  const [syncBaseline, setSyncBaseline] = useState(true);
  const [mode, setMode] = useState("edit");
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [translating, setTranslating] = useState(false);
  const editorApiRef = useRef(null);
  const [drafts, setDrafts] = useState(readStoredDrafts);
  const [draftDirty, setDraftDirty] = useState(false);
  const [writingOwn, setWritingOwn] = useState(false);
  const fileRef = useRef(null);
  const previousIdRef = useRef(null);
  const snapshotRef = useRef({});
  const draftsRef = useRef(drafts);
  const pendingDraftRef = useRef(null);
  const lastSentRef = useRef("");
  const draftEpochRef = useRef(0);
  draftsRef.current = drafts;

  snapshotRef.current = {
    saved,
    form: { name, description, category, title, html },
    draftDirty,
    creating,
    selectedId,
    language,
    translationExists: !!detail?.translations?.some((item) => item.language === language),
  };

  const languageMeta =
    languages.find((item) => item.code === language) || {
      code: language,
      name: language,
      native_name: language,
      dir: "ltr",
    };

  const hasUnsavedEdits = () => {
    const { saved: base, form } = snapshotRef.current;
    return (
      form.name !== base.name ||
      (form.description || "") !== (base.description || "") ||
      form.category !== base.category ||
      form.title !== base.title ||
      form.html !== base.html
    );
  };

  const isDirty = () => hasUnsavedEdits() || snapshotRef.current.draftDirty;

  const dropLocalDraft = useCallback((templateId, code) => {
    setDrafts((prev) => {
      const nextForTemplate = { ...(prev[templateId] || {}) };
      delete nextForTemplate[code];
      return { ...prev, [templateId]: nextForTemplate };
    });
    setTemplates((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(templateId)) return item;
        return {
          ...item,
          draft_languages: (item.draft_languages || []).filter((itemCode) => itemCode !== code),
        };
      }),
    );
    const pending = pendingDraftRef.current;
    if (pending && String(pending.id) === String(templateId) && pending.language === code) {
      pendingDraftRef.current = null;
    }
  }, []);

  const rememberDraft = useCallback(async (templateId, code, draftTitle, draftHtml) => {
    const epoch = draftEpochRef.current;
    const res = await apiFetch(`${apiBase}/${templateId}/drafts/${code}`, {
      method: "PUT",
      keepalive: true,
      body: JSON.stringify({
        title: (draftTitle || "").trim(),
        body_html: draftHtml || EMPTY_HTML,
      }),
    });
    if (epoch !== draftEpochRef.current) return null;
    if (res.status === 204) {
      dropLocalDraft(templateId, code);
      return null;
    }
    if (!res.ok) throw new Error(await readError(res, "Could not keep this draft"));
    const savedDraft = await res.json();
    setDrafts((prev) => ({
      ...prev,
      [templateId]: {
        ...(prev[templateId] || {}),
        [code]: {
          title: savedDraft.title || "",
          body_html: savedDraft.body_html || EMPTY_HTML,
          dir: savedDraft.dir,
        },
      },
    }));
    setTemplates((prev) =>
      prev.map((item) => {
        if (String(item.id) !== String(templateId)) return item;
        const codes = new Set(item.draft_languages || []);
        codes.add(code);
        return { ...item, draft_languages: [...codes] };
      }),
    );
    return savedDraft;
  }, [apiBase, dropLocalDraft]);

  const forgetDraft = useCallback(async (templateId, code) => {
    const res = await apiFetch(`${apiBase}/${templateId}/drafts/${code}`, { method: "DELETE" });
    if (!res.ok && res.status !== 404) {
      throw new Error(await readError(res, "Could not discard this draft"));
    }
    dropLocalDraft(templateId, code);
  }, [apiBase, dropLocalDraft]);

  const flushDraft = useCallback(async () => {
    const pending = pendingDraftRef.current;
    if (!pending) return;
    pendingDraftRef.current = null;
    const signature = `${pending.id}\n${pending.language}\n${pending.title}\n${pending.html}`;
    lastSentRef.current = signature;
    try {
      await rememberDraft(pending.id, pending.language, pending.title, pending.html);
    } catch (error) {
      lastSentRef.current = "";
      pendingDraftRef.current = pending;
      throw error;
    }
  }, [rememberDraft]);

  const dirty = isDirty();
  const translationExists = !!detail?.translations?.some((item) => item.language === language);
  const english = detail?.translations?.find((item) => item.language === "en");
  const currentTranslation = detail?.translations?.find((item) => item.language === language);

  const applyDetail = useCallback((tpl, code, nextMode = "edit") => {
    const preferred = tpl.translations?.find((item) => item.language === code)
      || tpl.translations?.find((item) => item.language === "en")
      || tpl.translations?.[0];
    const nextLanguage = preferred?.language || "en";
    const nextTitle = preferred?.title || "";
    const nextHtml = preferred?.body_html || EMPTY_HTML;
    const nextDescription = tpl.description || "";
    setCreating(false);
    setDetail(tpl);
    setSelectedId(tpl.id);
    setLanguage(nextLanguage);
    setName(tpl.name || "");
    setDescription(nextDescription);
    setCategory(tpl.category || "other");
    setTitle(nextTitle);
    setHtml(nextHtml);
    setSaved({
      name: tpl.name || "",
      description: nextDescription,
      category: tpl.category || "other",
      title: nextTitle,
      html: nextHtml,
    });
    setDraftDirty(false);
    setWritingOwn(false);
    setSyncBaseline(true);
    setEditorKey((value) => value + 1);
    setMode(nextMode);
    setDrafts((prev) => ({
      ...prev,
      [tpl.id]: {
        ...(prev[tpl.id] || {}),
        ...Object.fromEntries(
          (tpl.drafts || []).map((item) => [
            item.language,
            {
              title: item.title || "",
              body_html: item.body_html || EMPTY_HTML,
              dir: item.dir,
            },
          ]),
        ),
      },
    }));
  }, []);

  const loadList = useCallback(async () => {
    const res = await apiFetch(apiBase);
    if (!res.ok) throw new Error(await readError(res, "Failed to load templates"));
    return res.json();
  }, [apiBase]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await loadList();
        if (cancelled) return;
        setLanguages(data.languages || []);
        setTemplates(data.templates || []);
        if (data.templates?.length) {
          setDetailLoading(true);
          const res = await apiFetch(`${apiBase}/${data.templates[0].id}`);
          if (!res.ok) throw new Error(await readError(res, "Failed to open template"));
          const tpl = await res.json();
          if (!cancelled) applyDetail(tpl, "en");
        }
      } catch (error) {
        if (!cancelled) showNotification(error.message || "Failed to load templates", "error");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setDetailLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiBase, applyDetail, loadList, showNotification]);

  useEffect(() => {
    const stored = readStoredDrafts();
    const entries = [];
    Object.entries(stored).forEach(([templateId, langs]) => {
      Object.entries(langs || {}).forEach(([code, draft]) => {
        if (!draft) return;
        entries.push({ templateId, code, draft });
      });
    });
    if (!entries.length) return undefined;
    let cancelled = false;
    (async () => {
      const keep = new Set();
      await Promise.all(entries.map(async ({ templateId, code, draft }) => {
        try {
          await rememberDraft(templateId, code, draft.title || "", draft.body_html || EMPTY_HTML);
        } catch (error) {
          const message = error?.message || "";
          if (!/not found|choose a language/i.test(message)) {
            keep.add(`${templateId}:${code}`);
          }
        }
      }));
      const remaining = {};
      entries.forEach(({ templateId, code, draft }) => {
        if (!keep.has(`${templateId}:${code}`)) return;
        remaining[templateId] = { ...(remaining[templateId] || {}), [code]: draft };
      });
      try {
        if (Object.keys(remaining).length) {
          sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(remaining));
        } else {
          sessionStorage.removeItem(DRAFT_STORAGE_KEY);
        }
      } catch {
        // The server copy is already stored.
      }
      if (cancelled) return;
      try {
        const data = await loadList();
        setLanguages(data.languages || []);
        setTemplates(data.templates || []);
      } catch {
        // The list already on screen stays as it is.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadList, rememberDraft]);

  useEffect(() => {
    const onHide = () => {
      const pending = pendingDraftRef.current;
      if (!pending) return;
      const token = localStorage.getItem("access_token");
      const base = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
      fetch(`${base}${apiBase}/${pending.id}/drafts/${pending.language}`, {
        method: "PUT",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          title: (pending.title || "").trim(),
          body_html: pending.html || EMPTY_HTML,
        }),
      }).catch(() => {});
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [apiBase]);

  useEffect(() => {
    if (creating || saving || translating || !selectedId || translationExists) {
      if (saving || translationExists) pendingDraftRef.current = null;
      return undefined;
    }
    const plain = (html || "").replace(/<[^>]+>/g, "").replace(/\u00a0/g, " ").trim();
    if (!title.trim() && !plain) {
      pendingDraftRef.current = null;
      if (!draftsRef.current[selectedId]?.[language]) return undefined;
      const timer = setTimeout(() => {
        forgetDraft(selectedId, language).catch(() => {});
      }, 800);
      return () => clearTimeout(timer);
    }
    const signature = `${selectedId}\n${language}\n${title}\n${html}`;
    if (lastSentRef.current === signature) {
      pendingDraftRef.current = null;
      return undefined;
    }
    pendingDraftRef.current = { id: selectedId, language, title, html };
    const timer = setTimeout(() => {
      flushDraft().catch(() => {
        showNotification("Could not keep this draft. It is still on this page.", "error");
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [
    creating,
    saving,
    translating,
    selectedId,
    translationExists,
    language,
    title,
    html,
    flushDraft,
    forgetDraft,
    showNotification,
  ]);

  const guardDirty = async () => {
    const snap = snapshotRef.current;
    const draftOnly = !snap.creating && snap.selectedId && !snap.translationExists;
    if (draftOnly && pendingDraftRef.current) {
      try {
        await flushDraft();
      } catch {
        showNotification("Could not keep this draft. Stay on this page and try again.", "error");
        return false;
      }
    }
    const { saved: base, form } = snap;
    const metaDirty =
      form.name !== base.name ||
      (form.description || "") !== (base.description || "") ||
      form.category !== base.category;
    const officialTextDirty = snap.translationExists && (form.title !== base.title || form.html !== base.html);
    const creatingDirty = snap.creating && hasUnsavedEdits();
    if (!metaDirty && !officialTextDirty && !creatingDirty) return true;
    const ok = await confirmAction({
      title: "Discard unsaved changes?",
      text: "This template has edits that are not saved yet.",
      confirmText: "Discard",
      cancelText: "Keep editing",
      icon: "warning",
    });
    if (!ok) return false;
    setName(base.name);
    setDescription(base.description || "");
    setCategory(base.category);
    return true;
  };

  const openTemplate = async (id, nextMode = "edit") => {
    if (translating) return;
    if (!creating && id === selectedId && nextMode === "edit") return;
    if (!(await guardDirty())) return;
    setDetailLoading(true);
    try {
      const res = await apiFetch(`${apiBase}/${id}`);
      if (!res.ok) throw new Error(await readError(res, "Failed to open template"));
      applyDetail(await res.json(), "en", nextMode);
    } catch (error) {
      showNotification(error.message || "Failed to open template", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadTemplateFile = async (id, languageCode, fallbackName) => {
    try {
      const res = await apiFetch(`${apiBase}/${id}/translations/${languageCode}/docx`);
      if (!res.ok) throw new Error(await readError(res, "Could not download the Word file"));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filenameFromResponse(res, `${fallbackName}-${languageCode}.docx`);
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      showNotification(error.message || "Download failed", "error");
    }
  };

  const startCreate = async () => {
    if (translating) return;
    if (!(await guardDirty())) return;
    previousIdRef.current = selectedId;
    setCreating(true);
    setDetail(null);
    setSelectedId(null);
    setLanguage("en");
    setName("");
    setDescription("");
    setCategory("regulatory");
    setTitle("");
    setHtml(EMPTY_HTML);
    setSaved({
      name: "",
      description: "",
      category: "regulatory",
      title: "",
      html: EMPTY_HTML,
    });
    setDraftDirty(false);
    setWritingOwn(false);
    setSyncBaseline(true);
    setEditorKey((value) => value + 1);
    setMode("edit");
  };

  const showDraft = (code, draft) => {
    const nextTitle = draft?.title || "";
    const nextHtml = draft?.body_html || EMPTY_HTML;
    lastSentRef.current = `${selectedId}\n${code}\n${nextTitle}\n${nextHtml}`;
    pendingDraftRef.current = null;
    setLanguage(code);
    setTitle(nextTitle);
    setHtml(nextHtml);
    setSaved((prev) => ({ ...prev, title: nextTitle, html: nextHtml }));
    setDraftDirty(true);
    setWritingOwn(false);
    setSyncBaseline(true);
    setEditorKey((value) => value + 1);
    setMode("edit");
  };

  const switchLanguage = async (code) => {
    if (code === language || translating) return;
    if (creating) {
      setLanguage(code);
      setDraftDirty(false);
      setWritingOwn(false);
      setSyncBaseline(true);
      setEditorKey((value) => value + 1);
      return;
    }
    if (!(await guardDirty())) return;
    const savedTranslation = detail?.translations?.find((item) => item.language === code);
    const draft = drafts[selectedId]?.[code];
    if (!savedTranslation && draft) {
      showDraft(code, draft);
      return;
    }
    const nextTitle = savedTranslation?.title || "";
    const nextHtml = savedTranslation?.body_html || EMPTY_HTML;
    setLanguage(code);
    setTitle(nextTitle);
    setHtml(nextHtml);
    setSaved((prev) => ({ ...prev, title: nextTitle, html: nextHtml }));
    setDraftDirty(false);
    setWritingOwn(false);
    setSyncBaseline(true);
    setEditorKey((value) => value + 1);
    setMode("edit");
  };

  const translateFromEnglish = async () => {
    if (!english?.title && !english?.body_html) {
      showNotification("Save the English notice first, then translate it.", "error");
      return;
    }
    const plain = (html || "").replace(/<[^>]+>/g, "").replace(/\u00a0/g, " ").trim();
    if (translationExists && (title.trim() || plain)) {
      const ok = await confirmAction({
        title: `Replace the ${languageMeta.name} text?`,
        text: `The current ${languageMeta.name} wording will be replaced with a translation of the English notice. You can edit it before saving.`,
        confirmText: "Translate",
        cancelText: "Cancel",
        icon: "warning",
      });
      if (!ok) return;
    }
    setTranslating(true);
    try {
      const res = await apiFetch(`${apiBase}/translate`, {
        method: "POST",
        body: JSON.stringify({
          source_language: "en",
          target_language: language,
          title: english.title || "",
          body_html: english.body_html || "",
        }),
      });
      if (!res.ok) throw new Error(await readError(res, "Translation failed"));
      const data = await res.json();
      const nextTitle = data.title || "";
      const nextHtml = data.body_html || EMPTY_HTML;
      setTitle(nextTitle);
      setHtml(nextHtml);
      setSyncBaseline(false);
      setEditorKey((value) => value + 1);
      setMode("edit");
      if (!translationExists && selectedId) {
        lastSentRef.current = `${selectedId}\n${language}\n${nextTitle}\n${nextHtml}`;
        pendingDraftRef.current = null;
        try {
          await rememberDraft(selectedId, language, nextTitle, nextHtml);
        } catch (error) {
          showNotification(error.message || "Could not keep this draft.", "error");
        }
        setDraftDirty(true);
      }
      showNotification(
        `A ${languageMeta.name} draft is ready. It stays if you close this page. Read it, then save.`,
        "success",
      );
    } catch (error) {
      showNotification(error.message || "Translation failed", "error");
    } finally {
      setTranslating(false);
    }
  };

  const refreshTemplates = async () => {
    const data = await loadList();
    setLanguages(data.languages || []);
    setTemplates(data.templates || []);
    return data;
  };

  const handleSave = async () => {
    if (!name.trim() || !title.trim()) {
      showNotification("Enter a template name and a title for this language.", "error");
      return;
    }
    setSaving(true);
    if (!creating && selectedId && !translationExists) {
      pendingDraftRef.current = { id: selectedId, language, title, html };
      try {
        await flushDraft();
      } catch {
        // Save still sends this wording. The draft stays on the server if that save fails.
      }
    }
    draftEpochRef.current += 1;
    try {
      if (creating) {
        const res = await apiFetch(apiBase, {
          method: "POST",
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            category,
            language,
            title: title.trim(),
            body_html: html,
          }),
        });
        if (!res.ok) throw new Error(await readError(res, "Could not save template"));
        const created = await res.json();
        await refreshTemplates();
        applyDetail(created, language);
        showNotification("Template saved.", "success");
        return;
      }

      const metaChanged =
        name !== saved.name ||
        (description || "") !== (saved.description || "") ||
        category !== saved.category;
      if (metaChanged) {
        const res = await apiFetch(`${apiBase}/${selectedId}`, {
          method: "PATCH",
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
            category,
          }),
        });
        if (!res.ok) throw new Error(await readError(res, "Could not update template"));
      }

      const res = await apiFetch(`${apiBase}/${selectedId}/translations/${language}`, {
        method: "PUT",
        body: JSON.stringify({ title: title.trim(), body_html: html }),
      });
      if (!res.ok) throw new Error(await readError(res, "Could not save this language"));
      const savedTranslation = await res.json();
      const detailRes = await apiFetch(`${apiBase}/${selectedId}`);
      if (!detailRes.ok) throw new Error(await readError(detailRes, "Saved, but the template could not be reloaded"));
      const fresh = await detailRes.json();
      await refreshTemplates();
      setDetail(fresh);
      setName(fresh.name || "");
      setDescription(fresh.description || "");
      setCategory(fresh.category || "other");
      setTitle(savedTranslation.title);
      setHtml(savedTranslation.body_html || EMPTY_HTML);
      setSaved({
        name: fresh.name || "",
        description: fresh.description || "",
        category: fresh.category || "other",
        title: savedTranslation.title,
        html: savedTranslation.body_html || EMPTY_HTML,
      });
      if ((savedTranslation.body_html || EMPTY_HTML) !== html) {
        setSyncBaseline(true);
        setEditorKey((value) => value + 1);
      }
      setDraftDirty(false);
      dropLocalDraft(selectedId, language);
      lastSentRef.current = "";
      pendingDraftRef.current = null;
      showNotification(
        translationExists ? "Template updated." : "Language version saved.",
        "success",
      );
    } catch (error) {
      showNotification(error.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const copyTemplate = async (id) => {
    if (!id || copying || translating) return;
    if (!(await guardDirty())) return;
    setCopying(true);
    try {
      const res = await apiFetch(`${apiBase}/${id}/copy`, { method: "POST" });
      if (!res.ok) throw new Error(await readError(res, "Could not copy this template"));
      const created = await res.json();
      await refreshTemplates();
      applyDetail(created, "en");
      showNotification("Copy created. The original is unchanged.", "success");
    } catch (error) {
      showNotification(error.message || "Could not copy this template", "error");
    } finally {
      setCopying(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedId) return;
    const ok = await confirmAction({
      title: "Delete this template?",
      text: `"${name}" and every language version of it will be removed.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: "warning",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`${apiBase}/${selectedId}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await readError(res, "Could not delete template"));
      const data = await refreshTemplates();
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[selectedId];
        return next;
      });
      showNotification("Template deleted.", "success");
      if (data.templates?.length) {
        const next = await apiFetch(`${apiBase}/${data.templates[0].id}`);
        if (next.ok) applyDetail(await next.json(), "en");
      } else {
        setDetail(null);
        setSelectedId(null);
        setCreating(false);
      }
    } catch (error) {
      showNotification(error.message || "Could not delete template", "error");
    }
  };

  const handleDeleteLanguage = async () => {
    if (!translationExists) {
      const fallback = detail?.translations?.[0]?.language || "en";
      await switchLanguage(fallback);
      return;
    }
    const ok = await confirmAction({
      title: `Delete the ${languageMeta.name} version?`,
      text: `Only the ${languageMeta.name} text of "${name}" is removed. The template and its other languages stay.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      icon: "warning",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`${apiBase}/${selectedId}/translations/${language}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(await readError(res, "Could not delete this language"));
      const detailRes = await apiFetch(`${apiBase}/${selectedId}`);
      if (!detailRes.ok) throw new Error(await readError(detailRes, "Template could not be reloaded"));
      const fresh = await detailRes.json();
      await refreshTemplates();
      applyDetail(fresh, fresh.translations?.[0]?.language || "en");
      showNotification("Language version deleted.", "success");
    } catch (error) {
      showNotification(error.message || "Could not delete this language", "error");
    }
  };

  const handleDownload = async () => {
    if (!selectedId || !translationExists) {
      showNotification("Save this language before downloading it.", "error");
      return;
    }
    await downloadTemplateFile(selectedId, language, detail?.code || "template");
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedId) return;
    if (!file.name.toLowerCase().endsWith(".docx")) {
      showNotification("Upload a .docx file.", "error");
      return;
    }
    const ok = await confirmAction({
      title: translationExists ? "Replace this translation?" : "Create this translation from Word?",
      text: translationExists
        ? `The ${languageMeta.name} text of "${name}" will be replaced with ${file.name}. The template and its other languages stay as they are.`
        : `${file.name} will be saved as the ${languageMeta.name} version of "${name}".`,
      confirmText: translationExists ? "Replace" : "Upload",
      cancelText: "Cancel",
      icon: "warning",
    });
    if (!ok) return;
    setSaving(true);
    draftEpochRef.current += 1;
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await apiFetch(`${apiBase}/${selectedId}/translations/${language}/docx`, {
        method: "POST",
        body,
      });
      if (!res.ok) throw new Error(await readError(res, "Could not import the Word file"));
      const savedTranslation = await res.json();
      const detailRes = await apiFetch(`${apiBase}/${selectedId}`);
      if (!detailRes.ok) throw new Error(await readError(detailRes, "Imported, but the template could not be reloaded"));
      const fresh = await detailRes.json();
      const titleDirty = snapshotRef.current.form.title !== snapshotRef.current.saved.title;
      const nextTitle = titleDirty ? snapshotRef.current.form.title : savedTranslation.title;
      await refreshTemplates();
      setDetail(fresh);
      setName(fresh.name && snapshotRef.current.form.name === snapshotRef.current.saved.name ? fresh.name : snapshotRef.current.form.name);
      setTitle(nextTitle);
      setHtml(savedTranslation.body_html || EMPTY_HTML);
      setSaved((prev) => ({
        ...prev,
        name: fresh.name || prev.name,
        description: fresh.description || "",
        category: fresh.category || prev.category,
        title: savedTranslation.title,
        html: savedTranslation.body_html || EMPTY_HTML,
      }));
      setSyncBaseline(true);
      setEditorKey((value) => value + 1);
      setMode("edit");
      dropLocalDraft(selectedId, language);
      lastSentRef.current = "";
      pendingDraftRef.current = null;
      showNotification(
        translationExists ? "Translation replaced from Word." : "Translation created from Word.",
        "success",
      );
    } catch (error) {
      showNotification(error.message || "Upload failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const [query, setQuery] = useState("");
  const [listOpen, setListOpen] = useState(true);

  const savedCodes = new Set((detail?.translations || []).map((item) => item.language));
  const templateDrafts = (!creating && selectedId && drafts[selectedId]) || {};
  const currentDraft = !translationExists && templateDrafts[language];
  const keptLanguages = languages.filter(
    (item) => !creating && (savedCodes.has(item.code) || templateDrafts[item.code]),
  );
  const showNoticeEditor = creating || language === "en" || translationExists || !!currentDraft || writingOwn;

  const discardCurrentDraft = async () => {
    if (!selectedId || translationExists) return;
    const ok = await confirmAction({
      title: `Discard the ${languageMeta.name} draft?`,
      text: "This unsaved translation will be removed. Saved languages stay.",
      confirmText: "Discard draft",
      cancelText: "Keep it",
      icon: "warning",
    });
    if (!ok) return;
    draftEpochRef.current += 1;
    try {
      await forgetDraft(selectedId, language);
      lastSentRef.current = "";
      setTitle("");
      setHtml(EMPTY_HTML);
      setSaved((prev) => ({ ...prev, title: "", html: EMPTY_HTML }));
      setDraftDirty(false);
      setWritingOwn(false);
      setSyncBaseline(true);
      setEditorKey((value) => value + 1);
      showNotification("Draft discarded.", "success");
    } catch (error) {
      showNotification(error.message || "Could not discard this draft", "error");
    }
  };

  const q = query.trim().toLowerCase();
  const visible = templates.filter(
    (item) =>
      !q ||
      (item.name || "").toLowerCase().includes(q) ||
      categoryLabel(item.category).toLowerCase().includes(q),
  );
  const knownCategories = new Set(CATEGORIES.map((c) => c.value));
  const groups = CATEGORIES.map((c) => ({
    ...c,
    items: visible.filter(
      (t) => t.category === c.value || (c.value === "other" && !knownCategories.has(t.category)),
    ),
  })).filter((g) => g.items.length);

  const insertPlaceholder = (token) => {
    if (mode !== "edit") {
      setMode("edit");
    }
    const inserted = editorApiRef.current?.insertToken?.(token);
    if (inserted) {
      showNotification(`${token} inserted.`, "success");
      return;
    }
    setHtml((prev) => {
      const base = prev && prev !== EMPTY_HTML ? prev : "<p></p>";
      if (base.endsWith("</p>")) {
        return `${base.slice(0, -4)}${token}</p>`;
      }
      return `${base}<p>${token}</p>`;
    });
    showNotification(`${token} added to the notice.`, "success");
  };

  const restoreSamples = async () => {
    if (restoring || translating || copying) return;
    if (!(await guardDirty())) return;
    const ok = await confirmAction({
      title: "Restore sample notices?",
      text: "Any missing sample templates are added again. Notices you already have stay as they are.",
      confirmText: "Restore samples",
      cancelText: "Cancel",
      icon: "question",
    });
    if (!ok) return;
    setRestoring(true);
    try {
      const res = await apiFetch(`${apiBase}/restore-samples`, { method: "POST" });
      if (!res.ok) throw new Error(await readError(res, "Could not restore samples"));
      const data = await res.json();
      setLanguages(data.languages || []);
      setTemplates(data.templates || []);
      const count = data.restored_count || 0;
      if (count === 0) {
        showNotification("All sample notices are already present.", "success");
      } else {
        showNotification(
          count === 1 ? "1 sample notice restored." : `${count} sample notices restored.`,
          "success",
        );
      }
      if (!creating && selectedId) {
        const stillThere = (data.templates || []).some((item) => item.id === selectedId);
        if (stillThere) {
          const detailRes = await apiFetch(`${apiBase}/${selectedId}`);
          if (detailRes.ok) applyDetail(await detailRes.json(), language);
        } else if (data.templates?.length) {
          const detailRes = await apiFetch(`${apiBase}/${data.templates[0].id}`);
          if (detailRes.ok) applyDetail(await detailRes.json(), "en");
        }
      }
    } catch (error) {
      showNotification(error.message || "Could not restore samples", "error");
    } finally {
      setRestoring(false);
    }
  };

  const previewTitle = mode === "view" ? fillSamplePlaceholders(title) : title;
  const previewHtml = mode === "view" ? fillSamplePlaceholders(html || EMPTY_HTML) : (html || EMPTY_HTML);

  const placeholderChips = (
    <div className="space-y-1.5">
      <p className="text-xs text-slate-500">
        Placeholders (tap to insert{mode === "view" ? "; switch to Edit first" : " at the cursor"}):
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {PLACEHOLDERS.map((item) => (
          <button
            key={item.token}
            type="button"
            title={item.token}
            onClick={() => insertPlaceholder(item.token)}
            className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] text-slate-700 hover:bg-slate-100"
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );

  const editor = (
    <RichTemplateEditor
      key={`${editorKey}:${language}:${languageMeta.dir}`}
      ref={editorApiRef}
      initialHtml={html}
      dir={languageMeta.dir}
      onChange={setHtml}
      onReady={(normalized) => {
        setHtml(normalized);
        if (!syncBaseline) return;
        setSyncBaseline(false);
        setSaved((prev) => ({ ...prev, html: normalized }));
      }}
    />
  );

  const sectionTitle = "text-sm font-semibold text-slate-800";

  return (
    <CollapsibleSettingsCard
      tourId="tour-tenant-settings-breach-templates"
      title="Breach Notice Templates"
      description="Write a breach notice once, then add the same notice in other languages. Each language is stored separately."
      icon={<FileText size={18} className="text-white" />}
      iconClassName="bg-gradient-to-br from-rose-500 to-orange-600"
    >
      <style>{`
        .breach-template-editor{overflow:visible;isolation:isolate}
        .breach-template-editor .ql-toolbar.ql-snow{border:0;border-bottom:1px solid #e2e8f0;background:#f8fafc;display:flex;flex-wrap:wrap;gap:2px;overflow:visible;position:relative;z-index:3}
        .breach-template-editor .ql-container.ql-snow{border:0;font-size:15px;overflow:hidden}
        .breach-template-editor .ql-editor{min-height:260px;max-height:60vh;overflow-y:auto;line-height:1.65}
        .breach-template-editor.is-compare,.breach-template-editor.is-compare .breach-template-quill-host{display:flex;flex-direction:column;min-height:0;flex:1;height:100%}
        .breach-template-editor.is-compare .ql-toolbar.ql-snow{flex-shrink:0}
        .breach-template-editor.is-compare .ql-container.ql-snow{flex:1;min-height:0;display:flex;flex-direction:column}
        .breach-template-editor.is-compare .ql-editor{min-height:0;max-height:none;flex:1;height:100%}
        .breach-template-editor.is-compare .ql-snow.ql-container{border-radius:0 0 0.5rem 0.5rem}
        .breach-template-editor.rtl .ql-editor{text-align:right}
        .breach-template-editor .ql-snow .ql-picker{position:relative}
        .breach-template-editor .ql-snow .ql-picker-options{z-index:40}
        .breach-template-preview.is-compare{min-height:0;flex:1;overflow-y:auto}
        .breach-template-preview .sample-filled{background:rgba(99,102,241,0.08);border-radius:0.2rem;padding:0 0.15rem}
        .breach-compare-title{width:100%;border:1px solid #e2e8f0;border-radius:0.5rem;padding:0.5rem 0.75rem;font-size:0.875rem;line-height:1.4;color:#0f172a;outline:none}
        .breach-compare-title:focus{border-color:#818cf8;box-shadow:0 0 0 3px rgba(99,102,241,0.15)}
      `}</style>

      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-16 bg-slate-100 rounded-xl" />
          <div className="h-40 bg-slate-100 rounded-xl" />
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          {listOpen && (
          <aside className="w-full lg:w-52 xl:w-56 lg:shrink-0 lg:sticky lg:top-4 rounded-2xl border border-slate-200 bg-slate-50/70">
            <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2.5">
              <p className="text-sm font-semibold text-slate-800">Templates</p>
              <button
                type="button"
                onClick={() => setListOpen(false)}
                className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                Hide
              </button>
            </div>
            <div className="p-3 space-y-3">
              <Button
                type="button"
                className="w-full"
                onClick={async () => {
                  await startCreate();
                }}
                leftIcon={<Plus size={15} />}
              >
                New template
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                onClick={restoreSamples}
                disabled={restoring || copying || translating}
                loading={restoring}
                leftIcon={<RotateCcw size={15} />}
              >
                Restore samples
              </Button>

              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search templates"
                  aria-label="Search templates"
                  className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                />
              </div>

              {templates.length === 0 && (
                <p className="text-sm text-slate-500 px-1">No templates yet. Create one to reuse it on later breaches.</p>
              )}
              {templates.length > 0 && groups.length === 0 && (
                <p className="text-sm text-slate-500 px-1">No templates match “{query}”.</p>
              )}

              <div className="space-y-4 max-h-[60vh] lg:max-h-[640px] overflow-y-auto pr-1">
                {groups.map((group) => (
                  <section key={group.value} className="space-y-1.5">
                    <h3 className="flex items-center justify-between px-1 text-xs font-medium text-slate-500">
                      {group.label}
                      <span className="rounded-full bg-slate-200/70 px-1.5 text-[11px] text-slate-600">{group.items.length}</span>
                    </h3>
                    {group.items.map((item) => {
                      const active = !creating && item.id === selectedId;
                      const reviewCount = new Set([
                        ...(item.draft_languages || []),
                        ...Object.keys(drafts[item.id] || {}),
                      ]).size;
                      return (
                        <div
                          key={item.id}
                          className={`rounded-xl border px-3 py-2.5 ${
                            active
                              ? "border-indigo-300 bg-white shadow-sm ring-1 ring-indigo-200"
                              : "border-transparent bg-white/70"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => openTemplate(item.id)}
                            aria-current={active ? "true" : undefined}
                            className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 rounded-lg"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-800 break-words">{item.name}</p>
                              {item.is_predefined && <Badge variant="primary">Sample</Badge>}
                            </div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {reviewCount > 0 && (
                                <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800">
                                  {reviewCount} to review
                                </span>
                              )}
                              {(item.languages || []).length ? (
                                item.languages.map((code) => {
                                  const meta = languages.find((lang) => lang.code === code);
                                  return (
                                    <span key={code} className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">
                                      {meta?.name || code}
                                    </span>
                                  );
                                })
                              ) : (
                                <span className="text-[11px] text-slate-400">No language yet</span>
                              )}
                            </div>
                          </button>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(item.languages || []).length > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openTemplate(item.id, "view")}
                                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                >
                                  Preview
                                </button>
                                <button
                                  type="button"
                                  onClick={() => downloadTemplateFile(
                                    item.id,
                                    (item.languages || []).includes("en") ? "en" : item.languages[0],
                                    item.code || item.name || "template",
                                  )}
                                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                >
                                  Download
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => copyTemplate(item.id)}
                              disabled={copying}
                              className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                              Copy
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </section>
                ))}
              </div>
            </div>
          </aside>
          )}

          {/* ───────── Editor ───────── */}
          <div className="min-w-0 flex-1 rounded-2xl border border-slate-200 bg-white relative">
            {!listOpen && (
              <div className="border-b border-indigo-100 bg-indigo-50 px-4 py-3">
                <button
                  type="button"
                  onClick={() => setListOpen(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                >
                  <PanelLeft size={16} />
                  Show templates
                </button>
                <p className="mt-1.5 text-xs text-indigo-900">Open the list to switch a notice or create a new one.</p>
              </div>
            )}
            {detailLoading ? (
              <div className="p-5">
                <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
              </div>
            ) : !creating && !detail ? (
              <div className="p-10 text-center">
                <FileText size={28} className="mx-auto text-slate-300" />
                <p className="mt-2 text-sm text-slate-500">Select a template or create a new one.</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 sm:px-5 py-3">
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold text-slate-900 truncate">
                      {creating ? "New template" : name || "Untitled template"}
                    </h2>
                    <p className="text-xs text-slate-500">{categoryLabel(category)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {!creating && detail && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => copyTemplate(detail.id)}
                        disabled={copying}
                        loading={copying}
                        leftIcon={<Copy size={14} />}
                      >
                        {detail.is_predefined ? "Copy sample" : "Make a copy"}
                      </Button>
                    )}
                    {dirty && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 px-2.5 py-1 text-xs text-amber-800">
                        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-3 sm:p-4 pb-5 space-y-4 relative z-0">
                  {/* Details */}
                  <section className="space-y-2.5">
                    <h3 className={sectionTitle}>Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input label="Template name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Board notification" />
                      <Select label="Category" value={category} onChange={(event) => setCategory(event.target.value)}>
                        {CATEGORIES.map((item) => (
                          <option key={item.value} value={item.value}>{item.label}</option>
                        ))}
                      </Select>
                    </div>
                    <Input
                      label="Description"
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder="When this notice should be used"
                    />
                  </section>

                  {/* Languages */}
                  <section className="space-y-3">
                    <h3 className={`${sectionTitle} inline-flex items-center gap-2`}>
                      <Languages size={15} /> Which language are you writing?
                    </h3>
                    <p className="text-sm text-slate-500">
                      Pick a language. English and that language stay side by side.
                    </p>
                    <div className="space-y-1.5">
                      <label htmlFor="breach-template-language" className="text-sm font-medium text-slate-700">
                        Language
                      </label>
                      <select
                        id="breach-template-language"
                        value={language}
                        disabled={translating}
                        onChange={(event) => switchLanguage(event.target.value)}
                        className="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:cursor-not-allowed disabled:bg-slate-50"
                      >
                        {languages.map((item) => {
                          const native = item.native_name && item.native_name !== item.name ? ` — ${item.native_name}` : "";
                          return (
                            <option key={item.code} value={item.code}>
                              {item.name}{native}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                    {keptLanguages.length > 0 && (
                      <div className="flex flex-wrap gap-2" aria-label="Saved and draft languages">
                        {keptLanguages.map((item) => {
                          const active = item.code === language;
                          return (
                            <button
                              key={item.code}
                              type="button"
                              onClick={() => switchLanguage(item.code)}
                              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${
                                active
                                  ? "bg-indigo-600 text-white border-indigo-600"
                                  : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                              }`}
                            >
                              {item.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {languageMeta.dir === "rtl" && (
                      <p className="text-xs text-amber-800">
                        {languageMeta.name} is written from right to left, so the text box lines up on the right.
                      </p>
                    )}
                  </section>

                  {/* Content */}
                  <section className="space-y-3">
                    {!creating && language !== "en" && !english && (
                      <p className="text-sm text-slate-600 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                        Save the English notice first. Other languages are translated from that text.
                      </p>
                    )}

                    {!creating && language !== "en" && english && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 lg:grid-cols-2 lg:min-h-[28rem] lg:h-[min(62vh,36rem)] rounded-xl border border-slate-200 overflow-hidden bg-white">
                          <div className="min-w-0 min-h-0 flex flex-col bg-slate-50 border-b lg:border-b-0 lg:border-r border-slate-200">
                            <div className="shrink-0 px-3 py-2 border-b border-slate-200">
                              <p className="text-sm font-semibold text-slate-800">English</p>
                            </div>
                            <div className="px-3 py-2.5 space-y-1.5 flex-1 min-h-0 overflow-y-auto">
                              <p className="text-sm font-semibold text-slate-900 leading-snug">{english.title || "No English title yet"}</p>
                              <div
                                className="breach-template-preview is-compare text-sm text-slate-700"
                                dangerouslySetInnerHTML={{ __html: english.body_html || EMPTY_HTML }}
                              />
                            </div>
                          </div>
                          <div className="min-w-0 min-h-0 flex flex-col bg-white overflow-visible">
                            <div className="shrink-0 px-3 py-2 border-b border-slate-200 flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-800 mr-auto">
                                {languageMeta.name}
                                {languageMeta.native_name !== languageMeta.name ? ` · ${languageMeta.native_name}` : ""}
                              </p>
                              {showNoticeEditor && (
                                <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                                  {[
                                    { id: "edit", label: "Edit", icon: <Pencil size={13} /> },
                                    { id: "view", label: "Preview", icon: <Eye size={13} /> },
                                  ].map((tab) => (
                                    <button
                                      key={tab.id}
                                      type="button"
                                      onClick={() => setMode(tab.id)}
                                      aria-pressed={mode === tab.id}
                                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs ${
                                        mode === tab.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                                      }`}
                                    >
                                      {tab.icon} {tab.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {showNoticeEditor ? (
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={translateFromEnglish}
                                  loading={translating}
                                  disabled={translating}
                                >
                                  Translate again
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={translateFromEnglish}
                                  loading={translating}
                                  disabled={translating}
                                >
                                  Translate
                                </Button>
                              )}
                            </div>
                            <div className={`px-3 py-2.5 flex-1 min-h-0 flex flex-col gap-2 ${mode === "edit" && showNoticeEditor ? "overflow-visible" : "overflow-y-auto"}`}>
                              {!showNoticeEditor && (
                                <div className="my-auto py-6 text-center">
                                  <p className="text-sm text-slate-600">The English notice on the left is what gets translated.</p>
                                  <button
                                    type="button"
                                    className="mt-2 text-sm font-medium text-indigo-700 underline underline-offset-2"
                                    onClick={() => setWritingOwn(true)}
                                  >
                                    Write {languageMeta.name} yourself
                                  </button>
                                </div>
                              )}
                              {showNoticeEditor && (
                                <>
                                  {currentDraft && (
                                    <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5">
                                      <p className="text-xs text-amber-900">Draft is kept if you close the page.</p>
                                      <button
                                        type="button"
                                        className="shrink-0 text-xs font-medium text-amber-950 underline underline-offset-2"
                                        onClick={discardCurrentDraft}
                                      >
                                        Discard
                                      </button>
                                    </div>
                                  )}
                                  {mode === "view" ? (
                                    <p className="shrink-0 text-sm font-semibold text-slate-900 leading-snug" dir={languageMeta.dir} dangerouslySetInnerHTML={{ __html: previewTitle || "Untitled" }} />
                                  ) : (
                                    <input
                                      aria-label={`Title in ${languageMeta.name}`}
                                      value={title}
                                      dir={languageMeta.dir}
                                      onChange={(event) => setTitle(event.target.value)}
                                      placeholder={`Title in ${languageMeta.name}`}
                                      className="breach-compare-title shrink-0"
                                    />
                                  )}
                                  <div className={`min-h-0 flex-1 flex flex-col rounded-lg border border-slate-200 bg-white ${mode === "edit" ? "overflow-visible" : "overflow-hidden"}`}>
                                    <div className={mode === "edit" ? `breach-template-editor is-compare min-h-0 flex-1 ${languageMeta.dir === "rtl" ? "rtl" : ""}` : "hidden"}>
                                      {editor}
                                    </div>
                                    {mode === "view" && (
                                      <div
                                        dir={languageMeta.dir}
                                        className={`breach-template-preview is-compare px-3 py-2.5 text-slate-800 ${languageMeta.dir === "rtl" ? "rtl" : ""}`}
                                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                                      />
                                    )}
                                  </div>
                                  {mode === "view" && (
                                    <p className="shrink-0 text-[11px] text-slate-500">Preview uses sample values. They are not saved.</p>
                                  )}
                                  {currentTranslation?.updated_at && (
                                    <p className="shrink-0 text-[11px] text-slate-400">
                                      Saved {formatWhen(currentTranslation.updated_at)}
                                    </p>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        {showNoticeEditor && mode === "edit" && placeholderChips}
                      </div>
                    )}

                    {(creating || language === "en") && showNoticeEditor && (
                    <>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <h3 className={sectionTitle}>{language === "en" ? "English notice" : `${languageMeta.name} notice`}</h3>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
                          {[
                            { id: "edit", label: "Edit", icon: <Pencil size={14} /> },
                            { id: "view", label: "Preview", icon: <Eye size={14} /> },
                          ].map((tab) => (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() => setMode(tab.id)}
                              aria-pressed={mode === tab.id}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm ${
                                mode === tab.id ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              {tab.icon} {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {!creating && currentDraft && language === "en" && (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5">
                        <p className="text-sm text-amber-900">This draft is kept if you close the page. Save it when it is ready.</p>
                        <button
                          type="button"
                          className="shrink-0 text-sm font-medium text-amber-950 underline underline-offset-2"
                          onClick={discardCurrentDraft}
                        >
                          Discard draft
                        </button>
                      </div>
                    )}

                    {mode === "edit" ? (
                      <Input
                        label={`Title in ${languageMeta.name}`}
                        value={title}
                        dir={languageMeta.dir}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder={`Write the ${languageMeta.name} title`}
                      />
                    ) : (
                      <p className="text-sm font-semibold text-slate-900" dir={languageMeta.dir} dangerouslySetInnerHTML={{ __html: previewTitle || "Untitled" }} />
                    )}

                    <div className={`rounded-xl border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-indigo-500/20 ${mode === "edit" ? "overflow-visible" : "overflow-hidden"}`}>
                      <div className={mode === "edit" ? `breach-template-editor ${languageMeta.dir === "rtl" ? "rtl" : ""}` : "hidden"}>
                        {editor}
                      </div>
                      {mode === "view" && (
                        <div
                          dir={languageMeta.dir}
                          className={`breach-template-preview px-4 py-3 min-h-[260px] text-slate-800 ${languageMeta.dir === "rtl" ? "rtl" : ""}`}
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                      )}
                    </div>

                    {mode === "edit" && placeholderChips}
                    {mode === "view" && (
                      <p className="text-xs text-slate-500">Preview fills placeholders with sample values. They are not saved.</p>
                    )}
                    {currentTranslation?.updated_at && (
                      <p className="text-xs text-slate-400">
                        Last saved {formatWhen(currentTranslation.updated_at)}
                        {currentTranslation.updated_by ? ` by ${currentTranslation.updated_by}` : ""}.
                      </p>
                    )}
                    </>
                    )}
                  </section>
                </div>

                {/* Action bar */}
                <div className="sticky bottom-0 z-30 rounded-b-2xl border-t border-slate-200 bg-white px-4 sm:px-5 py-3 space-y-3 shadow-[0_-8px_16px_-12px_rgba(15,23,42,0.25)]">
                  {showNoticeEditor && (
                  <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2">
                    <Button
                      type="button"
                      className="col-span-2 sm:col-span-1"
                      onClick={handleSave}
                      loading={saving}
                      disabled={saving || (!creating && !dirty)}
                    >
                      {creating ? "Save template" : "Save changes"}
                    </Button>
                    <Button type="button" variant="secondary" onClick={handleDownload} disabled={creating || !translationExists} leftIcon={<Download size={15} />}>
                      Download Word
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => fileRef.current?.click()}
                      disabled={creating || saving}
                      leftIcon={<Upload size={15} />}
                    >
                      {translationExists ? "Replace from Word" : "Upload Word"}
                    </Button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden"
                      onChange={handleUpload}
                    />
                    {creating && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          if (previousIdRef.current) openTemplate(previousIdRef.current);
                          else setCreating(false);
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                  )}

                  {!creating && detail && showNoticeEditor && (
                    <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                      <span className="text-xs text-slate-500 mr-auto">Delete</span>
                      {translationExists && (detail?.translations?.length || 0) > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={handleDeleteLanguage} leftIcon={<Trash2 size={14} />}>
                          Delete {languageMeta.name}
                        </Button>
                      )}
                      <Button type="button" variant="danger" size="sm" onClick={handleDeleteTemplate} leftIcon={<Trash2 size={14} />}>
                        Delete template
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </CollapsibleSettingsCard>
  );
}